import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowLeft,
  Award,
  Calendar,
  Download,
  ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";
import { useData } from "@/contexts/DataContext";
import DomPurifyContent from "@/utils/domPurifyContent";
import CertificateMediaViewer from "@/components/ui/CertificateMediaViewer";
import CertificatePdfThumbnail from "@/components/ui/CertificatePdfThumbnail";
import {
  getCertificateFileUrl,
  isPdfFile,
} from "@/utils/certificateFile";

const CertificationDetailSkeleton = () => (
  <div className="min-h-screen py-12">
    <div className="container mx-auto px-4 max-w-5xl space-y-8">
      <div className="h-10 w-40 bg-muted animate-pulse rounded" />
      <div className="aspect-[4/3] w-full bg-muted animate-pulse rounded-xl" />
      <div className="space-y-3">
        <div className="h-6 w-32 bg-muted animate-pulse rounded" />
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded" />
        <div className="h-24 w-full bg-muted animate-pulse rounded" />
      </div>
    </div>
  </div>
);

const CertificationDetail = () => {
  const { id } = useParams();
  const { getCertificationById, certifications } = useData();
  const [certification, setCertification] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) return;
      const response = await getCertificationById(id);
      if (cancelled) return;
      if (!response) {
        setNotFound(true);
        setCertification(null);
      } else {
        setNotFound(false);
        setCertification(response);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, getCertificationById]);

  if (notFound) {
    return (
      <div className="min-h-screen py-20 text-center space-y-4">
        <Award className="w-12 h-12 mx-auto text-muted-foreground opacity-40" />
        <h1 className="text-2xl font-bold">Certification not found</h1>
        <Button variant="outline" asChild>
          <Link to="/certifications">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Certifications
          </Link>
        </Button>
      </div>
    );
  }

  if (!certification) return <CertificationDetailSkeleton />;

  const fileUrl = getCertificateFileUrl(certification.file);
  const related = (certifications || [])
    .filter(
      (c) =>
        c._id !== certification._id &&
        c.industry === certification.industry
    )
    .slice(0, 3);

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <Button variant="ghost" asChild>
            <Link to="/certifications">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Certifications
            </Link>
          </Button>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3 space-y-6"
          >
            <CertificateMediaViewer
              file={certification.file}
              title={certification.title}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 hover:bg-purple-100">
                  {certification.industry}
                </Badge>
                {certification.company && (
                  <Badge variant="outline">{certification.company}</Badge>
                )}
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {certification.issueDate
                    ? new Date(certification.issueDate).toLocaleDateString(
                        undefined,
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )
                    : "—"}
                </span>
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold leading-tight flex items-start gap-3">
                <Award className="w-8 h-8 text-purple-600 shrink-0 mt-1" />
                <span>{certification.title}</span>
              </h1>

              {certification.description && (
                <DomPurifyContent
                  className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground"
                  content={certification.description}
                />
              )}
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              {certification.verifyUrl?.trim() ? (
                <Button
                  className="bg-purple-600 hover:bg-purple-700 w-full"
                  asChild
                >
                  <a
                    href={certification.verifyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Verify Credential
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              ) : null}

              {fileUrl && (
                <Button variant="outline" className="w-full" asChild>
                  <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                    <Download className="w-4 h-4 mr-2" />
                    {isPdfFile(certification.file)
                      ? "Download PDF"
                      : "Open Image"}
                  </a>
                </Button>
              )}
            </div>
          </motion.div>
        </div>

        {related.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="mt-16 pt-12 border-t"
          >
            <h2 className="text-2xl font-bold mb-6">Related certifications</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((cert) => (
                <Card
                  key={cert._id}
                  className="overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  <Link to={`/certifications/${cert._id}`}>
                    <div className="aspect-[4/3] overflow-hidden bg-muted">
                      {isPdfFile(cert.file) ? (
                        <CertificatePdfThumbnail file={cert.file} />
                      ) : (
                        <img
                          src={getCertificateFileUrl(cert.file)}
                          alt={cert.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      )}
                    </div>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="text-xs">
                          {cert.industry}
                        </Badge>
                        {cert.company && (
                          <Badge variant="secondary" className="text-xs">
                            {cert.company}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold group-hover:text-purple-600 transition-colors line-clamp-2">
                        {cert.title}
                      </h3>
                    </CardContent>
                  </Link>
                </Card>
              ))}
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
};

export default CertificationDetail;
